import supplierService from '../../../services/supplierService';
import filterService from '../../../services/filterService';

import {
    setSupplierLoading,
    setSupplierError,
    setSupplierList,
    setSupplierCurrent,
    setSupplierPagination,
    setSupplierSort,
    setSupplierFilters,
    setSupplierDefaultFilter,
    setSupplierSettings,
    clearSupplierCurrent,
    clearSupplierError,
    setSupplierFilterLoading,
    changeSupplierFilterCondition,
    addSupplierFilterCondition,
    updateSupplierFilterConditionRule,
    updateSupplierFilterConditionValue,
    removeSupplierFilterCondition,
    setSupplierSelected,
    resetSupplierFiltersAndPagination
} from './supplierSlice';

export const fetchSettings = () => async (dispatch, getState) => {
    try {
        // Only fetch settings if they haven't been initialized
        if (!getState().supplier.isSettingsInitialized) {
            dispatch(setSupplierLoading(true));
            const response = await supplierService.getSettings();

            // Set the settings
            dispatch(setSupplierSettings(response));

            const activeFilter = response.filters.find(filter => filter.mark_default === true)
            if (activeFilter) {
                dispatch(setSupplierDefaultFilter(activeFilter));
                return activeFilter;
            }
        }
        return {};
    } catch (error) {
        console.error('Error fetching settings:', error);
        dispatch(setSupplierError({
            message: error.message || 'Failed to fetch settings',
            errors: error.errors || {}
        }));
        return {};
    } finally {
        dispatch(setSupplierLoading(false));
    }
};

export const fetchSuppliers = (params = {}, filter = {}, shouldUseFilter) => async (dispatch, getState) => {
    try {
        // Check if settings are initialized

        const state = getState().supplier;
        if (!state.isSettingsInitialized) {
            shouldUseFilter ? filter = await dispatch(fetchSettings()) : await dispatch(fetchSettings());
        }

        const finalQueryParams = {
            ...state.filters,
            page: state.pagination.current_page,
            per_page: state.pagination.per_page,
            sort_field: state.sort.field,
            sort_direction: state.sort.direction,
            ...params
        };

        const finalActiveFilter = shouldUseFilter ? (Object.keys(filter).length === 0 ? state.defaultFilter : { ...state.defaultFilter, ...filter }) : {};

        dispatch(setSupplierLoading(true));
        const response = await supplierService.getList(finalQueryParams, finalActiveFilter);
        dispatch(setSupplierList(response));
        return response;
    } catch (error) {
        console.error('Error in fetchSuppliers:', error);
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {},
            status: error.status || 500
        };
        dispatch(setSupplierError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setSupplierLoading(false));
    }
};

export const fetchSupplier = (id) => async (dispatch) => {
    try {
        dispatch(setSupplierLoading(true));
        const response = await supplierService.getOne(id);
        dispatch(setSupplierCurrent(response.data));
    } catch (error) {
        dispatch(setSupplierError({
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        }));
    } finally {
        dispatch(setSupplierLoading(false));
    }
};

export const createSupplier = (data) => async (dispatch) => {
    try {
        const response = await supplierService.create(data);
        return response.data || response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        throw serializedError;
    }
};

export const updateSupplier = (id, data) => async (dispatch) => {
    try {
        const response = await supplierService.update(id, data);
        return response.data || response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        throw serializedError;
    }
};

export const deleteSupplier = (id) => async (dispatch) => {
    try {
        dispatch(setSupplierLoading(true));
        const response = await supplierService.delete(id);
        return response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        dispatch(setSupplierError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setSupplierLoading(false));
    }
};

export const setPagination = (pagination) => (dispatch) => {
    dispatch(setSupplierPagination(pagination));
};

export const setSort = (sort) => (dispatch) => {
    dispatch(setSupplierSort(sort));
};

export const searchSuppliers = (query) => (dispatch, getState) => {
    const currentFilters = getState().supplier.filters;
    dispatch(setSupplierFilters({ ...currentFilters, search: query }));
};

// New filter-related actions
export const setDefaultFilter = (data) => {
    return async (dispatch) => {
        try {
            await dispatch(setSupplierDefaultFilter(data));
        } catch (error) {
            console.error('Error setting default filter:', error);
        }
    };
};

export const changeCondition = (condition) => {
    return (dispatch) => {
        dispatch(changeSupplierFilterCondition(condition));
    };
};

export const addNewCondition = (rule) => {
    return (dispatch) => {
        dispatch(addSupplierFilterCondition(rule));
    };
};

export const updateOptionRule = (index, data) => {
    return (dispatch) => {
        dispatch(updateSupplierFilterConditionRule({ index, data }));
    };
};

export const updateOptionValue = (index, value) => {
    return (dispatch) => {
        dispatch(updateSupplierFilterConditionValue({ index, value }));
    };
};

export const removeCondition = (index) => {
    return (dispatch) => {
        dispatch(removeSupplierFilterCondition(index));
    };
};

export const saveFilterCondition = (filterData) => async (dispatch, getState) => {
    try {
        dispatch(setSupplierFilterLoading(true));
        const response = await filterService.createFilterCondition(filterData);
        if (!response?.errors) {
            const currentSettings = getState().supplier.settings;
            dispatch(setSupplierSettings({ ...currentSettings, filters: response.filters }));
        }
        dispatch(setSupplierFilterLoading(false));
        return response;
    } catch (error) {
        console.error('Error saving filter:', error);
        dispatch(setSupplierFilterLoading(false));
        dispatch(setSupplierError({
            message: error.message || 'Failed to save filter',
            errors: error.errors || {}
        }));
        throw error;
    }
};

export const setSelected = (selectedRows) => {
    return (dispatch) => {
        dispatch(setSupplierSelected(selectedRows));
    };
};

export const clearCurrentSupplier = () => {
    return (dispatch) => {
        dispatch(clearSupplierCurrent());
    };
};

export const resetFiltersAndPagination = () => (dispatch) => {
    dispatch(resetSupplierFiltersAndPagination());
};